import React, { useState } from 'react';
import { Alert, Typography, Button, CircularProgress, Modal, Box } from '@mui/material';
import { useAuthProvider } from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import { FileDownload, Close } from '@mui/icons-material';
import networkAccessPrompt from './images/network-access-prompt.png';
import networkAccessSettings from './images/network-access-settings.png';
const R2PrefixLength = 'https://r2.devsoft.vn/'.length;
const apiUrl = process.env.REACT_APP_API_URL;
const download2 = async (authProvider, r2Url) => {
    let jwt = authProvider.getJWT();
    let r2Resource = r2Url.slice(R2PrefixLength);
    let response = await fetch(`${apiUrl}/r2/${r2Resource}`, {
        headers: {
            'authorization': `Bearer ${jwt}`,
        },
        method: 'GET',
    });
    var blob = await response.blob();
    return blob;
};
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};
const SetupInstruction = ({ error: msgError }) => {
    const [blob, setBlob] = useState(null);
    const r2Url = 'https://r2.devsoft.vn/SongPublic/BrowserStealth.78749.zip'; // Chrome 147
    const authProvider = useAuthProvider();
    const { mutate, isPending, isSuccess } = useMutation({
        mutationFn: () => download2(authProvider, r2Url).then(setBlob)
    });
    const reason = msgError.includes('Failed to fetch') 
    ? "The Stealth Service must run locally on your computer." 
    : msgError;
    return <>
        <Typography variant="h4" component="h4">Stealth Service Is Not Ready</Typography>
        {msgError && <Alert severity="error">{reason}</Alert>}
        <div>
            If you have already installed the software, please launch it and then refresh this page.
        </div>
        <br />
        <Typography variant="h4" component="h4">Installation</Typography>
        <h3>Step 1 — Download</h3>
        <div>
            <Button startIcon={<FileDownload />} size="medium" variant="outlined" onClick={mutate.bind(window)} disabled={isPending || isSuccess}>
                {isSuccess ? 'Download Complete' : (isPending ? <CircularProgress size={20} /> : 'Download')}
            </Button>
            <p>
                Click <strong>Download</strong> and wait for it to finish. Then extract the archive to any folder you
                prefer and run <strong>stealth.exe</strong> from that folder. Once it is running, refresh this page.
            </p>
        </div>
        <br />
        <h3>Step 2 — Allow Local Network Access</h3>
        <div>
            <section>
                <p>
                    The first time you run <strong>stealth.exe</strong>, your browser will ask for permission to
                    communicate with services on your local network. You must grant this permission for the
                    application to work correctly.
                </p>
                <ol>
                    <li>
                        <p>Click <strong>Allow</strong> when the browser prompts you:</p>
                        <img src={networkAccessPrompt} alt="Browser prompt asking for local network access permission" style={{ maxWidth: '100%', height: 'auto', marginTop: '10px' }} />
                    </li>
                    <li>
                        <p>
                            If you accidentally denied the request, you can re-enable access through your browser
                            settings. Click the lock icon in the address bar and turn on{' '}
                            <strong>Local network access</strong>:
                        </p>
                        <img src={networkAccessSettings} alt="Browser settings panel showing how to enable local network access" style={{ maxWidth: '100%', height: 'auto', marginTop: '10px' }} />
                    </li>
                </ol>
            </section>
        </div>
        <br />
        <h3>Step 3 — Run at Startup (Optional)</h3>
        <div>
            <section>
                <p>To have the Stealth Service start automatically with Windows:</p>
                <ol>
                    <li>
                        <p>
                            Press the <strong>Windows logo key</strong> + <strong>R</strong>, type{' '}
                            <strong>shell:startup</strong>, and click <strong>OK</strong>. This opens the Windows
                            Startup folder.
                        </p>
                    </li>
                    <li>
                        <p>
                            Create a shortcut to <strong>stealth.exe</strong> and place it in the Startup folder.
                            Windows will launch the service automatically each time you log in.
                        </p>
                    </li>
                </ol>
            </section>
        </div>

        {!!blob && <Modal
            open={true}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Save the Downloaded File
                </Typography>
                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                    <Button startIcon={<FileDownload />} size="medium" variant="outlined"
                        href={URL.createObjectURL(blob)}
                        download={r2Url.split('/').pop()}>Save</Button>
                    <Button startIcon={<Close />} size="medium" variant="outlined" color='warning' onClick={() => setBlob(null)}>Close</Button>
                </Typography>
            </Box>
        </Modal>
        }
    </>;
};
export default SetupInstruction;
